import { useEffect, useRef, useState } from 'react'

/**
 * URL をクリップボードにコピーするボタン。
 *
 * 設計意図:
 *   - クリックしてもユーザーに何も返ってこないと「効いたのか分からない」状態になるため、
 *     成功時にボタンのラベルを「コピーしました」に切り替え、色も成功色 (緑) に変える。
 *   - フィードバックはアクションの場所（ボタン自身）で返すのが Toast より直感的。
 *   - ラベル切替で幅が変動するとレイアウトがガタつくので、表示中・非表示中の両ラベルを
 *     CSS grid の同じセルに重ねて配置し、widest 分の幅を常に予約しておく。
 *   - 一部ブラウザでは Clipboard API が permission denied になることがあるが、
 *     URL は画面上に表示されている前提なので失敗時は黙ってデフォルトラベルに戻すだけ。
 */

type Variant = 'outline' | 'solid-primary'
type Size = 'sm' | 'md'

type Props = {
  url: string
  /** デフォルト時のラベル */
  label?: string
  variant?: Variant
  size?: Size
  disabled?: boolean
  /** 追加クラス（変動レイアウトの微調整用） */
  className?: string
}

const VARIANT_BASE: Record<Variant, string> = {
  outline: 'border border-border bg-background text-foreground hover:bg-muted',
  'solid-primary':
    'bg-primary text-primary-foreground hover:bg-primary/90 border border-transparent',
}

const VARIANT_COPIED: Record<Variant, string> = {
  outline: 'border border-emerald-500 bg-emerald-50 text-emerald-700',
  'solid-primary': 'bg-emerald-600 text-white border border-emerald-600',
}

const SIZE: Record<Size, string> = {
  sm: 'px-3 py-1.5 text-xs',
  md: 'px-4 py-2',
}

const COPIED_LABEL_TEXT = '✓ コピーしました'
const COPIED_DURATION_MS = 2000

export function CopyUrlButton({
  url,
  label = 'URLコピー',
  variant = 'outline',
  size = 'sm',
  disabled,
  className,
}: Props) {
  const [copied, setCopied] = useState(false)
  // unmount 後や連打時の遅延 setState を抑えるためタイマー ID を保持して掃除する
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [])

  const onClick = async () => {
    try {
      await navigator.clipboard.writeText(url)
      setCopied(true)
      if (timerRef.current) clearTimeout(timerRef.current)
      timerRef.current = setTimeout(() => setCopied(false), COPIED_DURATION_MS)
    } catch {
      // 一部ブラウザでは permission denied。URL は画面上に表示済みなので手動コピー可能。
    }
  }

  const stateClass = copied ? VARIANT_COPIED[variant] : VARIANT_BASE[variant]

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-live="polite"
      className={`rounded-md font-medium disabled:opacity-50 transition-colors ${SIZE[size]} ${stateClass} ${className ?? ''}`}
    >
      <span className="grid">
        <span aria-hidden className="col-start-1 row-start-1 invisible whitespace-nowrap">
          {label}
        </span>
        <span aria-hidden className="col-start-1 row-start-1 invisible whitespace-nowrap">
          {COPIED_LABEL_TEXT}
        </span>
        <span className="col-start-1 row-start-1 whitespace-nowrap">
          {copied ? COPIED_LABEL_TEXT : label}
        </span>
      </span>
    </button>
  )
}
